import { fetchWithTimeout } from '../../utils/promiseWithTimeout';
import { getWipayPaymentRequestUrl } from './wipayPlatform';
import { WIPAY_REQUEST_TIMEOUT_MS } from './wipayTimeouts';
import { createLogger } from '../../utils/logger';

const logger = createLogger('buildWipayRequest');

/**
 * Build application/x-www-form-urlencoded body for WiPay payment initiation.
 */
export function buildWipayRequestParams({
  accountNumber,
  environment,
  feeStructure,
  responseUrl,
  wipayOrderId,
  paymentAmount,
  wipayData,
  countryCode = 'JM',
  currency = 'JMD',
  cardType = 'visa',
}) {
  const parameters = new URLSearchParams();
  parameters.append('account_number', accountNumber);
  parameters.append('avs', '0');
  parameters.append('country_code', String(countryCode || 'JM').toUpperCase());
  parameters.append('currency', currency || 'JMD');
  parameters.append('data', JSON.stringify(wipayData));
  parameters.append('environment', environment);
  parameters.append('fee_structure', feeStructure);
  // WiPay docs: legacy 'credit_card' is deprecated; new integrations should use
  // 'credit_card_co' for the hosted checkout page.
  // WiPay docs: legacy 'credit_card' is deprecated; new integrations should use
  // 'credit_card_co' for the hosted checkout page.
  parameters.append('method', 'credit_card_co');
  // Optional: skip card selection page on WiPay hosted checkout
  if (cardType === 'visa' || cardType === 'mastercard') {
    parameters.append('card_type', cardType);
  }
  parameters.append('order_id', wipayOrderId);
  parameters.append('origin', 'PackMyCart');
  parameters.append('response_url', responseUrl);
  parameters.append('total', Number(paymentAmount).toFixed(2));
  return parameters;
}

/**
 * POST to WiPay and return parsed JSON response.
 * Uses the sandbox or live host that matches `environment` and `country_code`
 * (e.g. jmsb.wipayfinancial.com for JM sandbox — not jm.wipayfinancial.com).
 * @param {URLSearchParams} params
 * @param {{ timeoutMs?: number, countryCode?: string, environment?: string }} [options]
 */
export async function requestWipayPaymentUrl(params, options = {}) {
  const timeoutMs = options.timeoutMs ?? WIPAY_REQUEST_TIMEOUT_MS;
  const countryCode = options.countryCode || params.get('country_code') || 'JM';
  const environment = options.environment || params.get('environment') || 'sandbox';
  const requestUrl =
    options.requestUrl || getWipayPaymentRequestUrl(countryCode, environment);

  logger.debug('[requestWipayPaymentUrl] POST', requestUrl, {
    countryCode,
    environment,
    orderId: params.get('order_id'),
  });

  const headers = new Headers();
  headers.append('Accept', 'application/json');
  headers.append('Content-Type', 'application/x-www-form-urlencoded');

  const response = await fetchWithTimeout(
    requestUrl,
    {
      method: 'POST',
      headers,
      body: params,
      redirect: 'follow',
    },
    timeoutMs
  );
  const result = await response.text();

  if (!response.ok) {
    logger.error('[requestWipayPaymentUrl] WiPay HTTP error:', response.status, result);
    throw new Error(
      `WiPay payment request failed (${response.status}). Check environment and account_number.`
    );
  }

  const parsed = JSON.parse(result);
  if (parsed?.url) {
    logger.debug('[requestWipayPaymentUrl] Checkout redirect URL:', parsed.url);
  }
  return parsed;
}
